import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, UserPlus, CheckCircle, XCircle,
  MapPin, Phone, Mail, Loader2, Building2, RefreshCw, FlaskConical, Pill, Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DoctorLinkVendors({ token, onRefresh }) {
  const [subTab, setSubTab] = useState('connected');
  const [vendors, setVendors] = useState([]);
  const [connectedVendors, setConnectedVendors] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('');
  const [requesting, setRequesting] = useState(null);
  const [processing, setProcessing] = useState(null);
  const getHeaders = () => ({ Authorization: `Bearer ${token || localStorage.getItem('clinic_token')}` });

  const fetchAvailableVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (vendorTypeFilter && vendorTypeFilter !== 'all') params.vendor_type = vendorTypeFilter;
      if (areaFilter) params.area = areaFilter;
      const res = await axios.get(`${API}/api/vendor-doctor-mappings/doctor/available-vendors`, { headers: getHeaders(), params });
      setVendors(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchText, vendorTypeFilter, areaFilter, token]);

  const fetchIncomingRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-doctor-mappings/doctor/incoming-requests`, { headers: getHeaders() });
      setIncomingRequests(res.data || []);
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchConnectedVendors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-doctor-mappings/doctor/my-vendors`, { headers: getHeaders() });
      setConnectedVendors(res.data || []);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => {
    fetchAvailableVendors();
    fetchIncomingRequests();
    fetchConnectedVendors();
  }, []);

  const handleSearch = () => {
    fetchAvailableVendors();
  };

  const handleRequestConnect = async (vendorId) => {
    setRequesting(vendorId);
    try {
      await axios.post(`${API}/api/vendor-doctor-mappings/doctor/request-vendor`, { target_id: vendorId, notes: '' }, { headers: getHeaders() });
      toast.success('Connection request sent to vendor');
      fetchAvailableVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequesting(null);
    }
  };

  const handleAccept = async (mappingId) => {
    setProcessing(mappingId);
    try {
      await axios.put(`${API}/api/vendor-doctor-mappings/doctor/accept/${mappingId}`, {}, { headers: getHeaders() });
      toast.success('Connection approved');
      fetchIncomingRequests();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to accept');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (mappingId) => {
    setProcessing(mappingId);
    try {
      await axios.put(`${API}/api/vendor-doctor-mappings/doctor/reject/${mappingId}`, { reason: '' }, { headers: getHeaders() });
      toast.success('Connection declined');
      fetchIncomingRequests();
    } catch (err) {
      toast.error('Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getVendorIcon = (type) => {
    if (type === 'lab') return <FlaskConical className="w-5 h-5 text-purple-600" />;
    if (type === 'pharma') return <Pill className="w-5 h-5 text-blue-600" />;
    return <Package className="w-5 h-5 text-teal-600" />;
  };

  const getStatusBadge = (status) => {
    const map = {
      approved: { label: 'Connected', className: 'bg-green-100 text-green-700' },
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
      not_connected: { label: 'Not Connected', className: 'bg-slate-100 text-slate-500' }
    };
    const m = map[status] || { label: status, className: 'bg-slate-100 text-slate-500' };
    return <Badge className={m.className}>{m.label}</Badge>;
  };

  return (
    <div data-testid="doctor-link-vendors">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="connected" className="gap-1"><Building2 className="w-3.5 h-3.5" /> My Vendors</TabsTrigger>
          <TabsTrigger value="search" className="gap-1"><Search className="w-3.5 h-3.5" /> Find Vendors</TabsTrigger>
          <TabsTrigger value="incoming" className="gap-1">
            <UserPlus className="w-3.5 h-3.5" /> Incoming Requests
            {incomingRequests.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-[10px] px-1.5">{incomingRequests.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="connected">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Connected Vendors</CardTitle>
                <CardDescription>Your approved vendor connections with referral charge details</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={fetchConnectedVendors}><RefreshCw className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent>
              {connectedVendors.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No connected vendors yet. Search and connect with vendors.</p>
              ) : (
                <div className="space-y-2">
                  {connectedVendors.map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-slate-50 transition" data-testid="connected-vendor-item">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          {getVendorIcon(v.vendor_type)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{v.vendor_name || 'Unknown'}</p>
                          <div className="flex flex-wrap gap-2 mt-0.5">
                            {v.vendor_type && <span className="text-[10px] text-teal-600 bg-teal-50 px-1.5 rounded capitalize">{v.vendor_type}</span>}
                            {v.vendor_phone && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{v.vendor_phone}</span>}
                            {v.vendor_email && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{v.vendor_email}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                        {v.doctor_commission_pct > 0 ? (
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Referral Rate</p>
                            <p className="text-sm font-bold text-emerald-600" data-testid="vendor-commission-pct">{v.doctor_commission_pct}%</p>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No referral rate set</span>
                        )}
                        <Badge variant="outline" className="text-emerald-600 border-emerald-300">Connected</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Find & Connect with Vendors</CardTitle>
              <CardDescription>Search by name, phone, email, address, or vendor type</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div className="relative sm:col-span-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Name, phone, email, address..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 text-sm"
                    data-testid="doctor-search-vendors-input"
                  />
                </div>
                <Select value={vendorTypeFilter} onValueChange={setVendorTypeFilter}>
                  <SelectTrigger data-testid="doctor-vendor-type-filter">
                    <SelectValue placeholder="Vendor Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="lab">Lab</SelectItem>
                    <SelectItem value="pharma">Pharma</SelectItem>
                    <SelectItem value="chemist">Chemist</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input
                    placeholder="Area / City"
                    value={areaFilter}
                    onChange={(e) => setAreaFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="text-sm"
                    data-testid="doctor-area-filter"
                  />
                  <Button size="sm" onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                </div>
              ) : vendors.length === 0 ? (
                <div className="text-center py-8 text-slate-400">No vendors found matching your filters</div>
              ) : (
                <div className="space-y-2">
                  {vendors.map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-slate-50 transition" data-testid="vendor-search-result">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          {getVendorIcon(v.vendor_type)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{v.name}</p>
                          <div className="flex flex-wrap gap-2 mt-0.5">
                            {v.vendor_type && <span className="text-[10px] text-teal-600 bg-teal-50 px-1.5 rounded capitalize">{v.vendor_type}</span>}
                            {v.phone && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{v.phone}</span>}
                            {v.email && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{v.email}</span>}
                          </div>
                          {v.address && <p className="text-[10px] text-slate-400 flex items-center gap-0.5 mt-0.5"><MapPin className="w-2.5 h-2.5" />{v.address}</p>}
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        {v.connection_status === 'not_connected' ? (
                          <Button size="sm" onClick={() => handleRequestConnect(v.id)} disabled={requesting === v.id} data-testid="connect-vendor-btn">
                            {requesting === v.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                            Connect
                          </Button>
                        ) : (
                          getStatusBadge(v.connection_status)
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incoming">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Incoming Vendor Requests</CardTitle>
                <CardDescription>Vendors who want to connect with you</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={fetchIncomingRequests}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {incomingRequests.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No pending requests</p>
              ) : (
                <div className="space-y-2">
                  {incomingRequests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50/50" data-testid="incoming-vendor-request">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{req.vendor_name || 'Unknown Vendor'}</p>
                        {req.vendor_type && <span className="text-[10px] text-teal-600 bg-teal-50 px-1.5 rounded capitalize">{req.vendor_type}</span>}
                        <div className="flex flex-wrap gap-2 mt-0.5">
                          {req.vendor_phone && <span className="text-[10px] text-slate-400"><Phone className="w-2.5 h-2.5 inline mr-0.5" />{req.vendor_phone}</span>}
                          {req.vendor_email && <span className="text-[10px] text-slate-400"><Mail className="w-2.5 h-2.5 inline mr-0.5" />{req.vendor_email}</span>}
                        </div>
                        {req.vendor_address && <p className="text-[10px] text-slate-400"><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{req.vendor_address}</p>}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => handleAccept(req.id)} disabled={processing === req.id} className="bg-green-600 hover:bg-green-700" data-testid="accept-vendor-request-btn">
                          {processing === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                          Accept
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleReject(req.id)} disabled={processing === req.id} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-vendor-request-btn">
                          <XCircle className="w-3 h-3 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
